<template>
  <HeaderBar />

  <ButtonBackContainer>
    <ButtonBack :fallback="{ name: 'legalEntityList' }">{{ t('back') }}</ButtonBack>
  </ButtonBackContainer>

  <section class="carvia-container flex flex-col justify-center">
    <LegalEntityForm
      v-if="legalEntity || isNew"
      :key="legalEntity?.id ?? 'new'"
      :legal-entity="legalEntity"
      :is-loading="isCreating || isUpdating"
      :has-issued-invoices="!!legalEntity"
      @submit="handleSubmit"
    />
    <div v-else class="flex justify-center"><Spinner /></div>
  </section>
</template>

<script lang="ts" setup>
import { computed, ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import type { CreateLegalEntityDto, UpdateLegalEntityDto } from '@carvia/ros-client-types';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import Spinner from '@/components/icons/Spinner.vue';
import { useUiStore } from '@/stores/ui.store';
import {
  useCreateLegalEntity,
  useLegalEntity,
  useUpdateLegalEntity,
} from '@/queries/use-legal-entities';
import LegalEntityForm from './LegalEntityForm.vue';

const { t } = useI18n();
const uiStore = useUiStore();
const route = useRoute();
const router = useRouter();
const legalEntityId = ref(route.params.id as string);
const isNew = computed(() => legalEntityId.value === 'new');

const { data: legalEntity } = useLegalEntity(legalEntityId, () => !isNew.value);
const { mutateAsync: createLegalEntity, isPending: isCreating } = useCreateLegalEntity();
const { mutateAsync: updateLegalEntity, isPending: isUpdating } = useUpdateLegalEntity();

watchEffect(() => {
  uiStore.setHeaderTitle(t('legalEntity'), legalEntity.value?.name);
});

const handleSubmit = async (values: UpdateLegalEntityDto) => {
  if (isNew.value) {
    const { id } = await createLegalEntity(values as CreateLegalEntityDto);
    legalEntityId.value = id;
    await router.replace({ name: 'legalEntity', params: { id } });
  } else {
    await updateLegalEntity({ id: legalEntityId.value, legalEntity: values });
  }
};
</script>

<i18n lang="json">
{
  "en": { "legalEntity": "Legal entity", "back": "Back" },
  "de": { "legalEntity": "Gesellschaft", "back": "Zurück" },
  "es": { "legalEntity": "Sociedad", "back": "Volver" }
}
</i18n>
